/**
 * AI Aiming State Machine — drives turret aim, charge, and fire.
 *
 * States: THINKING → TARGET_SELECT → SOLVING → AIMING → CHARGING → FIRED
 *
 * AI injects keys via AIInput.setKey() — game logic sees it as normal input.
 * Same turret ramp speed, same charge oscillation, same fire pulse.
 */

import { Keys } from 'bonkjs';
import { ACTION } from '../constants';
import type { GameState } from '../types';
import type { AIInput } from './ai-input';
import type { SkillProfile } from './skill-profile';
import type { AimSolution } from './trajectory-solver';
import { solveTrajectory } from './trajectory-solver';
import { selectTarget } from './target-selection';
import { getDefaultWeapon } from '../weapons';

export type AimPhase = 'thinking' | 'target_select' | 'solving' | 'aiming' | 'charging' | 'fired';

export interface AimState {
  phase: AimPhase;
  thinkTimer: number;
  targetIndex: number;
  solution: AimSolution | null;
  chargeStarted: boolean;
  fireFrames: number; // frames to hold fire pulse
}

export function createAimState(): AimState {
  return {
    phase: 'thinking',
    thinkTimer: 0,
    targetIndex: -1,
    solution: null,
    chargeStarted: false,
    fireFrames: 0,
  };
}

export function resetAimState(aim: AimState, profile: SkillProfile): void {
  aim.phase = 'thinking';
  aim.thinkTimer = profile.thinkFrames;
  aim.targetIndex = -1;
  aim.solution = null;
  aim.chargeStarted = false;
  aim.fireFrames = 0;
}

/**
 * Per-frame aiming update. Returns true when the AI has fired.
 */
export function updateAimPhase(
  aim: AimState,
  state: GameState,
  playerIndex: number,
  input: AIInput,
  profile: SkillProfile,
): boolean {
  const player = state.players[playerIndex];

  // Clear previous frame's keys
  input.setKey(Keys.ArrowUp, false);
  input.setKey(Keys.ArrowDown, false);
  input.setKey(ACTION.CHARGE, false);
  input.setKey(ACTION.FIRE, false);

  switch (aim.phase) {
    case 'thinking': {
      aim.thinkTimer--;
      if (aim.thinkTimer <= 0) {
        if (import.meta.env.DEV) console.log(`[AI P${playerIndex}] thinking → target_select`);
        aim.phase = 'target_select';
      }
      break;
    }

    case 'target_select': {
      aim.targetIndex = selectTarget(state, playerIndex, profile);
      if (aim.targetIndex < 0) {
        // No targets — just fire straight up
        if (import.meta.env.DEV) console.log(`[AI P${playerIndex}] no targets → fire straight up`);
        aim.solution = { angle: Math.PI / 2, power: 0.5, score: -Infinity };
        aim.phase = 'aiming';
      } else {
        if (import.meta.env.DEV) console.log(`[AI P${playerIndex}] target → P${aim.targetIndex}`);
        aim.phase = 'solving';
      }
      break;
    }

    case 'solving': {
      const target = state.players[aim.targetIndex];
      const weapon = player.selectedWeapon ?? getDefaultWeapon();
      aim.solution = solveTrajectory(
        player.x, player.y,
        target.x, target.y,
        weapon, state.wind, profile, state.voidY,
      );
      if (import.meta.env.DEV) console.log(`[AI P${playerIndex}] solved: angle=${aim.solution.angle.toFixed(3)} power=${aim.solution.power.toFixed(3)} score=${aim.solution.score.toFixed(1)}`);
      aim.phase = 'aiming';
      break;
    }

    case 'aiming': {
      if (!aim.solution) { aim.phase = 'fired'; break; }

      const targetAngle = aim.solution.angle;
      const diff = targetAngle - player.angle;

      if (Math.abs(diff) <= profile.aimThreshold) {
        // Close enough — start charging
        if (import.meta.env.DEV) console.log(`[AI P${playerIndex}] aim locked → charging (angle=${player.angle.toFixed(3)} target=${targetAngle.toFixed(3)})`);
        aim.phase = 'charging';
        aim.chargeStarted = false;
        break;
      }

      // Inject aim direction keys — same ramp as human
      if (diff > 0) {
        input.setKey(Keys.ArrowUp, true);
      } else {
        input.setKey(Keys.ArrowDown, true);
      }
      break;
    }

    case 'charging': {
      if (!aim.solution) { aim.phase = 'fired'; break; }

      const targetPower = aim.solution.power;

      if (!aim.chargeStarted) {
        // First frame: press charge
        if (import.meta.env.DEV) console.log(`[AI P${playerIndex}] charge start (targetPower=${targetPower.toFixed(3)} playerCharging=${player.charging} playerPower=${player.power.toFixed(3)})`);
        input.setKey(ACTION.CHARGE, true);
        aim.chargeStarted = true;
        break;
      }

      // Keep holding charge while power oscillates
      // Release when power is near target
      const powerDiff = Math.abs(player.power - targetPower);
      if (powerDiff <= profile.powerThreshold && player.power > 0.05) {
        // Release charge by not pressing it, then fire
        if (import.meta.env.DEV) console.log(`[AI P${playerIndex}] FIRE! power=${player.power.toFixed(3)} target=${targetPower.toFixed(3)} diff=${powerDiff.toFixed(4)}`);
        aim.phase = 'fired';
        aim.fireFrames = 4; // pulse fire for 4 frames (matches ChargeStateMachine)
        input.setKey(ACTION.FIRE, true);
        break;
      }

      // Keep holding charge
      input.setKey(ACTION.CHARGE, true);
      break;
    }

    case 'fired': {
      if (aim.fireFrames > 0) {
        input.setKey(ACTION.FIRE, true);
        aim.fireFrames--;
        return false;
      }
      if (import.meta.env.DEV) console.log(`[AI P${playerIndex}] fire sequence complete`);
      return true; // done — fired
    }
  }

  return false;
}
